import { Outlet, Link, useLocation } from 'react-router-dom';
import { Shield, Search, Menu, X, LogOut, FileText, Newspaper, DollarSign, Radar, ShieldAlert, ShoppingBag, Home } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import GuestBanner from '@/components/slaw/GuestBanner';

const NAV_ITEMS = [
  { to: '/slaw-landing', label: 'Home', icon: Home },
  { to: '/reports', label: 'Reports', icon: ShoppingBag, highlight: true },
  { to: '/slaw-index', label: 'Command', icon: Shield },
  { to: '/award-intel', label: 'Awards', icon: DollarSign },
  { to: '/contract-dna', label: 'Contracts', icon: FileText },
  { to: '/opportunity-radar', label: 'Radar', icon: Radar },
  { to: '/exclusion-watch', label: 'Exclusions', icon: ShieldAlert, accent: 'rust' as const },
  { to: '/intelligence', label: 'Briefing', icon: Newspaper },
  { to: '/contracts', label: 'Feed', icon: Search },
];

export default function SLAWShell() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const previousPathRef = useRef(location.pathname);
  const returnFocusToMenuRef = useRef(false);

  const closeMobileMenu = () => {
    returnFocusToMenuRef.current = true;
    setMobileOpen(false);
    window.requestAnimationFrame(() => {
      menuButtonRef.current?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    setMobileOpen(wasOpen => {
      if (wasOpen) {
        window.requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }));
      }
      return false;
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    window.requestAnimationFrame(() => {
      const activeDrawerLink = document.querySelector<HTMLAnchorElement>(
        `nav.fixed a[href="${location.pathname}"]`
      );
      activeDrawerLink?.focus({ preventScroll: true });
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMobileOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }));
        return;
      }

      if (e.key !== 'Tab') return;

      const drawer = document.getElementById('slaw-mobile-navigation');
      const focusableItems = Array.from(
        drawer?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? []
      ).filter(item => item.offsetParent !== null);

      if (!drawer || focusableItems.length === 0) {
        e.preventDefault();
        return;
      }

      const firstItem = focusableItems[0];
      const lastItem = focusableItems[focusableItems.length - 1];
      const activeElement = document.activeElement;

      if (!drawer.contains(activeElement)) {
        e.preventDefault();
        firstItem.focus({ preventScroll: true });
        return;
      }

      if (e.shiftKey && activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus({ preventScroll: true });
        return;
      }

      if (!e.shiftKey && activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus({ preventScroll: true });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, location.pathname]);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;

    window.requestAnimationFrame(() => {
      if (returnFocusToMenuRef.current) {
        menuButtonRef.current?.focus({ preventScroll: true });
        returnFocusToMenuRef.current = false;
        return;
      }

      const activeHeaderLink = document.querySelector<HTMLAnchorElement>(
        `header nav a[href="${location.pathname}"]`
      );

      if (activeHeaderLink && activeHeaderLink.offsetParent !== null) {
        activeHeaderLink.focus({ preventScroll: true });
        return;
      }

      const heading = document.querySelector<HTMLElement>('main#main-content h1');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        if (mobileOpen) {
          closeMobileMenu();
        } else {
          window.requestAnimationFrame(() => {
            if (menuButtonRef.current?.offsetParent !== null) {
              menuButtonRef.current?.focus({ preventScroll: true });
            }
          });
        }
        setFocusMode(f => !f);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileOpen]);

  // Swap favicon for SLAW domain
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) link.href = '/slaw-favicon.png';
    return () => { if (link) link.href = '/favicon.png?v=10'; };
  }, []);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header — zero Boltway references */}
      <header className="h-14 flex items-center justify-between px-4 sm:px-6 bg-card border-b border-border shrink-0 relative z-50">
        <div className="flex items-center gap-4">
          <button
            ref={menuButtonRef}
            type="button"
            className="xl:hidden h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="slaw-mobile-navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <Link to="/slaw-landing" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Shield className="h-4 w-4 text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <span className="text-[15px] font-bold text-foreground tracking-tight">SLAW Score</span>
              <sup className="text-[8px] align-super opacity-60">™</sup>
              <p className="text-[10px] text-muted-foreground leading-none -mt-0.5">Entity Data Quality Standard</p>
            </div>
          </Link>

          <div className="hidden lg:block h-6 w-px bg-border" />

          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.to;
              const isHighlight = item.highlight;
              const isRust = (item as any).accent === 'rust';
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    "relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors",
                    active
                      ? isRust
                        ? "bg-rust/10 text-rust"
                        : isHighlight
                          ? "bg-success/10 text-success"
                          : "bg-primary/10 text-primary"
                      : isHighlight
                        ? "border border-success/40 text-muted-foreground hover:bg-success/10 hover:text-success"
                        : isRust
                          ? "text-rust/80 hover:bg-rust/10 hover:text-rust"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                  {isHighlight && !active && (
                    <span className="relative flex h-1.5 w-1.5 ml-0.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success" />
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/slaw-landing"
            className="text-[12px] font-semibold px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Audit Your Entity
          </Link>

          {user ? (
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 text-[13px] hover:bg-accent rounded-md px-2 py-1.5 transition-colors">
                  <div className="h-7 w-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-[12px] font-semibold text-primary">
                    {user.email?.[0]?.toUpperCase() ?? 'A'}
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48 p-1">
                <div className="px-3 py-2 border-b border-border mb-1">
                  <p className="text-[12px] font-medium text-foreground truncate">{user.email}</p>
                </div>
                <Link
                  to="/my-reports"
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-foreground hover:bg-accent rounded-md transition-colors"
                >
                  <FileText className="h-3.5 w-3.5" /> My Reports
                </Link>
                <button
                  onClick={signOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </PopoverContent>
            </Popover>
          ) : (
            <Link
              to="/auth"
              className="text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
            >
              Sign in
            </Link>
          )}
        </div>
      </header>

      {focusMode && (
        <div className="fixed top-2 right-2 z-50 opacity-30 hover:opacity-100 transition-opacity">
          <button
            onClick={() => setFocusMode(false)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-card border border-border text-[10px] text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Exit Focus (⌘.)
          </button>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 xl:hidden"
            onClick={closeMobileMenu}
          />
          <nav id="slaw-mobile-navigation" className="fixed top-14 left-0 w-64 h-[calc(100vh-3.5rem)] bg-card border-r border-border z-40 xl:hidden overflow-y-auto p-3 space-y-1">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  onClick={closeMobileMenu}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] font-medium transition-colors",
                    active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <item.icon className="h-4.5 w-4.5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </>
      )}

      <GuestBanner />

      <main id="main-content" className="flex-1 overflow-auto" role="main" aria-label="Page content">
        <Outlet />
      </main>

      {/* Footer — institutional authority, no Boltway */}
      {!focusMode && <footer className="h-10 hidden sm:flex items-center justify-between px-6 bg-card border-t border-border text-[11px] text-muted-foreground shrink-0">
        <div className="flex items-center gap-4">
          <span>© {new Date().getFullYear()} SLAW Score™ — Entity Integrity Standard</span>
          <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
        </div>
        <div className="flex items-center gap-4">
          <span>Powered by the Truth Engine™</span>
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-success" />
            <span>Systems Operational</span>
          </div>
        </div>
      </footer>}
    </div>
  );
}
